import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';
import type { Gebruiker, InlogAanvraag } from '@types';
import { login, logout } from '@services';
import { AUTH } from '@utils/constants';

// ============================================
// Auth Store Types
// ============================================

interface AuthState {
  // State
  gebruiker: Gebruiker | null;
  isIngelogd: boolean;
  hydrated: boolean;
  isLoading: boolean;
  error: string | null;
  
  // Actions
  inloggen: (credentials: InlogAanvraag) => Promise<void>;
  uitloggen: () => Promise<void>;
  clearError: () => void;
  setGebruiker: (gebruiker: Gebruiker) => void;
}

// ============================================
// Initial State
// ============================================

const initialState = {
  gebruiker: null,
  isIngelogd: false,
  hydrated: false,
  isLoading: false,
  error: null,
};

// ============================================
// Auth Store
// ============================================

/**
 * Zustand store voor authenticatie.
 * 
 * Verantwoordelijkheden:
 * - Gebruiker state beheren
 * - Login/logout functionaliteit
 * - Auth status checken
 * 
 * Regels (zie 01_FRONTEND_GUARDRAILS.md):
 * - Gebruikt Zustand voor state management
 * - Geen token opslag (alleen httpOnly cookies)
 * - Error handling voor alle async operaties
 */
export const useAuthStore = create<AuthState>()(
  devtools(
    persist(
      (set) => ({
        ...initialState,
        
        // Inloggen actie
        inloggen: async (credentials: InlogAanvraag) => {
          set({ isLoading: true, error: null });
          
          try {
            const response = await login(credentials);
            
            // Token wordt via HttpOnly cookie meegestuurd, alleen gebruiker opslaan
            set({
              gebruiker: response.gebruiker,
              isIngelogd: true,
              isLoading: false,
            });
            
            // Redirect naar sessies beheer
            window.location.href = AUTH.SESSIES_PATH;
            
          } catch (error) {
            // Error handling wordt gedaan door de api interceptor
            // Hier alleen de state updaten
            set({
              isLoading: false,
              error: 'Inloggen mislukt. Controleer je email en wachtwoord.',
            });
            throw error; // Re-throw voor component-level handling
          }
        },
        
        // Uitloggen actie
        uitloggen: async () => {
          set({ isLoading: true });
          
          try {
            await logout();
          } catch (error) {
            // Ook als uitloggen faalt, willen we de state resetten
            console.error('Uitloggen error:', error);
          } finally {
            set({
              ...initialState,
              isLoading: false,
            });
            
            // Redirect naar login
            window.location.href = AUTH.LOGIN_PATH;
          }
        },
        
        
        // Clear error
        clearError: () => {
          set({ error: null });
        },
        
        // Set gebruiker (voor SSO of andere auth flows)
        setGebruiker: (gebruiker: Gebruiker) => {
          set({
            gebruiker,
            isIngelogd: true,
          });
        },
        

      }),
      {
        name: 'auth-storage',
        partialize: (state) => ({
          // Alleen gebruiker en isIngelogd persisten (GEEN token om veiligheidsredenen)
          gebruiker: state.gebruiker,
          isIngelogd: state.isIngelogd,
        }),
        // Zet hydrated op true als persist hydratie klaar is
        merge: (persistedState: any, currentState: any) => {
          return {
            ...currentState,
            ...persistedState,
            hydrated: true,
          };
        },
      }
    ),
    { name: 'AuthStore' }
  )
);

// ============================================
// Selectors
// ============================================

export const selectGebruiker = (state: AuthState) => state.gebruiker;
export const selectIsIngelogd = (state: AuthState) => state.isIngelogd;
export const selectHydrated = (state: AuthState) => state.hydrated;
export const selectIsLoading = (state: AuthState) => state.isLoading;
export const selectError = (state: AuthState) => state.error;
